import React from 'react';
import { Icon } from '@instinet-analytics/semantic-ui-components';
import { textUtils } from '@instinet-analytics/core-lib';

import 'utils/components/FiltersModal/ActiveFilters.scss';
import {
    ColumnFilterState,
    FilterState,
    isColumnFilter,
} from 'widgets/store/grid/filters/FilterState';
import { ColumnDefinitionsDictionary } from '@instinet-analytics/semantic-ui-components';
import { filterToString } from 'utils/components/FiltersModal/filterToString';
import { EditedColumnNames } from 'grid/columnNames/hooks';

const { pluralise } = textUtils;

interface ActiveFiltersProps<TColumnId extends string> {
    columnDefinitions: ColumnDefinitionsDictionary<TColumnId>;
    editedColumnNames?: EditedColumnNames;
    pendingFilters: FilterState<TColumnId>[];
    editingColumn: TColumnId | undefined;
    noun?: string;
    onToggleEditingColumn: (column: TColumnId) => void;
    onDeleteFilter: (index: number) => void;
    onClearAllFilters: () => void;
}

export default function ActiveFilters<TColumnId extends string>({
    pendingFilters,
    editingColumn,
    editedColumnNames,
    noun,
    onToggleEditingColumn,
    onDeleteFilter,
    onClearAllFilters,
    columnDefinitions,
}: ActiveFiltersProps<TColumnId>) {
    const onDeleteClick = (index: number) => (event: Event) => {
        event.stopPropagation();
        onDeleteFilter(index);
    };

    const onFilterClick = (index: number) => () => {
        const column = (pendingFilters[index] as ColumnFilterState<TColumnId>)
            .column;
        if (isColumnFilter(pendingFilters[index])) {
            onToggleEditingColumn(column);
        }
    };

    const activeFilterContent = pendingFilters.length ? (
        pendingFilters.map((x, i) => (
            <div
                className="filter"
                key={i}
                onClick={onFilterClick(i)}
                data-selected={isEditing(x)}
            >
                <div className="filter-value">
                    {filterToString(
                        columnDefinitions,
                        x as ColumnFilterState<TColumnId>,
                        editedColumnNames
                    )}
                </div>
                <div className="filter-close">
                    <Icon
                        className="action"
                        name="close"
                        onClick={onDeleteClick(i)}
                    />
                </div>
            </div>
        ))
    ) : (
        <div className="empty-message">
            There are no active {noun ? pluralise(2, noun) : 'filters'}.
        </div>
    );

    return (
        <section className="ActiveFilters">
            <section className="title">
                <span>
                    Active {noun ? pluralise(2, noun) : 'filters'} (
                    {pendingFilters.length})
                </span>
                {!!pendingFilters.length && (
                    <button
                        className="ui button text-link Button"
                        onClick={onClearAllFilters}
                    >
                        Clear all
                    </button>
                )}
            </section>
            <section className="filter-container">
                {activeFilterContent}
            </section>
        </section>
    );

    function isEditing(f: FilterState<TColumnId>) {
        return (f as ColumnFilterState<TColumnId>).column === editingColumn;
    }
}
